import React from 'react';
import { observer } from 'mobx-react';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import styled from '@emotion/styled';
import { Theme } from '@seedlang/constants';
import { ArrowLeftIcon, SettingsIcon } from '@seedlang/icons';
import { pixify } from '@seedlang/utils';
import { AppUI } from '@seedlang/state';

const Wrapper = styled.div`
  margin-left: ${props => props.marginLeft};
  position: ${props => props.isMobile ? 'initial' : 'relative'};
  width: ${props => props.width};
  height: ${props => props.height};
  .settings-icon {
    cursor: pointer;
    background: #585858;
    height: 20px;
    width: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    svg {
      width: 12px;
      fill: #DDD;
    }
  }
`;

const Header = styled.div`
  background: #DDD;
  padding: 10px;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${props => props.isMobile ? '50px' : null};
  box-sizing: border-box;
  position: ${props => props.isMobile ? 'sticky' : 'relative'};
  top: 0;
  z-index: 1;
  flex-shrink: 0;
`;

const Title = styled.div`
  flex: 1;
  text-align: ${props => props.isMobile ? 'center' : 'left'};
  font-weight: bold;
`;

const BackButton = styled.button`
  position: absolute;
  left: 10px;
  width: 36px;
  height: 36px;
  padding: 9px;
  border: 0;
  background: transparent;
  cursor: pointer;
  .left-arrow-icon,
  svg {
    width: 18px;
    height: 18px;
    fill: #636363;
  }
`;

const Popup = styled.div`
  height: ${props => props.isMobile ? '100dvh' : props.height};
  width: ${props => props.isMobile ? '100vw' : props.width};
  top: ${props => props.isMobile ? 0 : '55px'};
  right: ${props => props.isMobile ? 0 : props.right};
  left: ${props => props.isMobile ? 0 : props.left};
  z-index: ${Theme.z['overlay-1']};
  background: white;
  position: ${props => props.isMobile ? 'fixed' : 'absolute'};
  box-shadow: 0px 2px 7px rgba(0,0,0,0.25);
  border-bottom-right-radius: ${props => props.isMobile ? 0 : '5px'};
  border-bottom-left-radius: ${props => props.isMobile ? 0 : '5px'};
  overflow-y: auto;
`;

const Triangle = styled.div`
  display: ${props => props.isMobile ? 'none' : 'block'};
  right: ${props => props.right};
  position: absolute;
  width: 0;
  height: 0;
  top: ${props => props.isMobile ? -5 : -10}px;
  border-style: solid;
  border-width: 0 15px 15px 15px;
  border-color: transparent transparent #DDD transparent;
  z-index: ${Theme.z['overlay-1']};
`;

const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 50%;
  font-size: 14px;
  height: ${props => props.width};
  width: ${props => props.width};
  background: ${props => props.background ? props.background : '#1499b7'};
`;

@observer
class HeaderPopup extends React.Component {
  constructor(props) {
    super(props);
    this.wrapperRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener('click', this.onClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.onClickOutside);
  }

  onClickOutside = event => {
    if (
      this.props.popupOpened &&
      this.wrapperRef.current &&
      !this.wrapperRef.current.contains(event.target)
    ) {
      this.props.onClickIcon();
    }
  }

  render() {
    return (
      <Wrapper
        ref={this.wrapperRef}
        isMobile={AppUI.layout.isMobile}
        width={AppUI.layout.isMobile ? '35px' : '42px'}
        height={this.props.height}
        marginLeft={AppUI.siteIsSeedlang ? '10px' : '15px'}
      >
        <IconWrapper
          onClick={this.props.onClickIcon}
          background={this.props.background}
          width={AppUI.layout.isMobile ? '35px' : '42px'}
        >
          {this.props.icon}
        </IconWrapper>
        {
          this.props.popupOpened &&
            <Popup
              isMobile={AppUI.layout.isMobile}
              height={pixify(this.props.height)}
              width={AppUI.layout.isMobile ? '100%' : pixify(this.props.width)}
              right={AppUI.layout.isMobile ? 'auto' : pixify(this.props.right)}
              left={AppUI.layout.isMobile ? '0' : 'auto'}
            >
              <Triangle
                isMobile={AppUI.layout.isMobile}
                right={pixify(this.props.arrowRight)}
              />
              <Header
                isMobile={AppUI.layout.isMobile}
              >
                {
                  AppUI.layout.isMobile &&
                  <BackButton
                    aria-label='Back'
                    onClick={this.props.onClickIcon}
                  >
                    <ArrowLeftIcon />
                  </BackButton>
                }
                <Title
                  isMobile={AppUI.layout.isMobile}
                >
                  {this.props.title}
                </Title>
                {
                  this.props.showSettings &&
                    <SettingsIcon
                      onClick={this.props.onClickSettings}
                    />
                }
              </Header>
              {this.props.children}
            </Popup>
        }
      </Wrapper>
    );
  }
}

export default ErrorBoundary(HeaderPopup);
